import { create } from 'zustand';
import { getPlanningEntries } from '../api/generated/planning-entries/planning-entries';
import { getPlanningSuggestions } from '../api/generated/planning-suggestions/planning-suggestions';
import type {
  FacilityFlowCoreModulesPlanningDtosAssistantAssistantEmployeeDto,
  FacilityFlowCoreModulesPlanningDtosAssistantPlanningSuggestionsDto,
  FacilityFlowCoreModulesPlanningDtosAssistantSuggestionFitDto,
} from '../api/generated/models';
import { showApiError } from '../errors';
import { isoDate } from '../utils/dateUtils';
import { usePlanningStore } from './usePlanningStore';

type SuggestionsDto = FacilityFlowCoreModulesPlanningDtosAssistantPlanningSuggestionsDto;
type SuggestionFit = FacilityFlowCoreModulesPlanningDtosAssistantSuggestionFitDto;
type AssistantEmployee = FacilityFlowCoreModulesPlanningDtosAssistantAssistantEmployeeDto;

export type PlanningAssistantMode = 'employee' | 'task' | 'map';

const MODE_STORAGE_KEY = 'ff.planning.assistant.mode';
const MODES: PlanningAssistantMode[] = ['employee', 'task', 'map'];

function loadMode(): PlanningAssistantMode {
  try {
    const stored = localStorage.getItem(MODE_STORAGE_KEY);
    return MODES.includes(stored as PlanningAssistantMode) ? (stored as PlanningAssistantMode) : 'employee';
  } catch {
    return 'employee';
  }
}

function saveMode(mode: PlanningAssistantMode) {
  try {
    localStorage.setItem(MODE_STORAGE_KEY, mode);
  } catch {
    // Storage unavailable (private mode, blocked site data): the mode stays per-session.
  }
}

function hhmm(minutes: number): string {
  const m = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
}

/** A start point override for one employee's day; `label` is shown in the assistant. */
export interface StartPointValue {
  latitude: number;
  longitude: number;
  label: string | null;
}

export interface LastAssigned {
  /** Increments per assignment so the undo toast restarts its timer. */
  seq: number;
  planningEntryId: string;
  employeeId: string;
  taskId: string;
  taskName: string;
  employeeName: string;
  start: number;
  end: number;
}

interface PlanningAssistantState {
  mode: PlanningAssistantMode;
  date: string;
  employeeIndex: number;
  doneEmployeeIds: string[];
  selectedTaskId: string | null;
  hiddenRouteEmployeeIds: string[];
  data: SuggestionsDto | null;
  loading: boolean;
  /** A silent refetch after assign/undo is running; the old data stays visible. */
  refreshing: boolean;
  /** An assign or undo request is in flight; further ones are blocked. */
  busy: boolean;
  error: string | null;
  newlyAssignedTaskIds: string[];
  lastAssigned: LastAssigned | null;

  setMode: (mode: PlanningAssistantMode) => void;
  /** Switches the day; resets the per-day progress and loads it. */
  setDate: (date: string) => void;
  setEmployeeIndex: (index: number) => void;
  markEmployeeDone: (employeeId: string) => void;
  selectTask: (taskId: string | null) => void;
  toggleRoute: (employeeId: string) => void;
  fetchDay: (date?: string, opts?: { silent?: boolean }) => Promise<void>;
  /** Plans the fit's task for the fit's employee. Resolves true on success. */
  assign: (fit: SuggestionFit) => Promise<boolean>;
  /** Removes the last assigned task again. */
  undo: () => Promise<void>;
  dismissLastAssigned: () => void;
  /**
   * Sets (value) or removes (null) the start point override of the employee's work day. Creates
   * the WorkDay entry when setting and none exists. Resolves true on success.
   */
  setStartPoint: (employeeId: string, value: StartPointValue | null) => Promise<boolean>;
}

const api = getPlanningSuggestions();
const entriesApi = getPlanningEntries();

/**
 * The employee's WorkDay entry id for `date`; creates the entry when missing. Same shape as
 * generateMonthEntries: the backend fills the window from the employee's work schedule model.
 */
async function ensureWorkDayEntry(employee: AssistantEmployee, date: string): Promise<string> {
  if (employee.planningEntryId) return employee.planningEntryId;
  const created = await usePlanningStore.getState().createEntry({
    employeeId: employee.employeeId ?? '',
    date,
    entryType: 'workday',
    startTime: null,
    endTime: null,
    breakMinutes: 0,
    targetHours: null,
    tasks: null,
    notes: null,
    absenceTypeId: null,
  });
  if (!created.planningEntryId) throw new Error('Planning entry missing');
  return created.planningEntryId;
}

// why: assign/undo trigger silent refetches that may overlap a day switch — only the
// newest request may write its result.
let requestSeq = 0;
let assignSeq = 0;

/** Removes one occurrence — the same task may have been assigned twice. */
function removeOnce(ids: string[], id: string): string[] {
  const i = ids.indexOf(id);
  return i < 0 ? ids : [...ids.slice(0, i), ...ids.slice(i + 1)];
}

/** Optimistic view of the day after planning `fit`: task gone from the open list, block added. */
function applyOptimistic(data: SuggestionsDto, fit: SuggestionFit): SuggestionsDto {
  const task = data.tasks?.find((t) => t.taskId === fit.taskId);
  const duration = (fit.end ?? 0) - (fit.start ?? 0);
  return {
    ...data,
    tasks: (data.tasks ?? []).filter((t) => t.taskId !== fit.taskId),
    fits: (data.fits ?? []).filter((f) => f.taskId !== fit.taskId),
    employees: (data.employees ?? []).map((e): AssistantEmployee => {
      if (e.employeeId !== fit.employeeId) return e;
      const blocks = [
        ...(e.blocks ?? []),
        {
          planningEntryTaskId: '',
          taskId: fit.taskId,
          taskName: task?.name ?? null,
          objectId: task?.objectId ?? null,
          objectName: task?.objectName ?? null,
          objectShortName: task?.objectShortName ?? null,
          start: fit.start ?? 0,
          end: fit.end ?? 0,
        },
      ].sort((a, b) => (a.start ?? 0) - (b.start ?? 0));
      return {
        ...e,
        blocks,
        plannedMinutes: (e.plannedMinutes ?? 0) + duration,
        freeMinutes: Math.max(0, (e.freeMinutes ?? 0) - duration),
      };
    }),
  };
}

export const usePlanningAssistantStore = create<PlanningAssistantState>((set, get) => ({
  mode: loadMode(),
  date: isoDate(new Date()),
  employeeIndex: 0,
  doneEmployeeIds: [],
  selectedTaskId: null,
  hiddenRouteEmployeeIds: [],
  data: null,
  loading: false,
  refreshing: false,
  busy: false,
  error: null,
  newlyAssignedTaskIds: [],
  lastAssigned: null,

  setMode: (mode) => {
    saveMode(mode);
    set({ mode });
  },

  setDate: (date) => {
    const changed = date !== get().date || !get().data;
    if (!changed) {
      // Same day again (page re-opened): keep progress, refresh without a skeleton.
      void get().fetchDay(date, { silent: true });
      return;
    }
    set({
      date,
      employeeIndex: 0,
      doneEmployeeIds: [],
      selectedTaskId: null,
      newlyAssignedTaskIds: [],
      lastAssigned: null,
    });
    void get().fetchDay(date);
  },

  setEmployeeIndex: (index) => set({ employeeIndex: Math.max(0, index) }),

  markEmployeeDone: (employeeId) =>
    set((s) => ({
      doneEmployeeIds: s.doneEmployeeIds.includes(employeeId)
        ? s.doneEmployeeIds
        : [...s.doneEmployeeIds, employeeId],
    })),

  selectTask: (taskId) => set({ selectedTaskId: taskId }),

  toggleRoute: (employeeId) =>
    set((s) => ({
      hiddenRouteEmployeeIds: s.hiddenRouteEmployeeIds.includes(employeeId)
        ? s.hiddenRouteEmployeeIds.filter((id) => id !== employeeId)
        : [...s.hiddenRouteEmployeeIds, employeeId],
    })),

  fetchDay: async (date, opts) => {
    const day = date ?? get().date;
    const seq = ++requestSeq;
    const silent = !!opts?.silent && get().data != null;
    set(silent ? { refreshing: true } : { loading: true, error: null, data: null });
    try {
      const data = await api.getApiV1PlanningSuggestions({ date: day });
      if (seq !== requestSeq) return;
      set({ data, error: null });
    } catch (err) {
      if (seq !== requestSeq) return;
      if (silent) {
        showApiError(err);
      } else {
        set({ error: 'assistant.errors.fetchFailed' });
      }
    } finally {
      if (seq === requestSeq) set({ loading: false, refreshing: false });
    }
  },

  assign: async (fit) => {
    const { data, date, busy } = get();
    if (!data || busy || fit.start == null || fit.end == null || !fit.taskId || !fit.employeeId) {
      return false;
    }
    const employee = data.employees?.find((e) => e.employeeId === fit.employeeId);
    const task = data.tasks?.find((t) => t.taskId === fit.taskId);
    if (!employee || !task) return false;

    const previous = data;
    const taskId = fit.taskId;
    set((s) => ({
      busy: true,
      data: applyOptimistic(data, fit),
      newlyAssignedTaskIds: [...s.newlyAssignedTaskIds, taskId],
      selectedTaskId: s.selectedTaskId === taskId ? null : s.selectedTaskId,
    }));

    try {
      const planningEntryId = await ensureWorkDayEntry(employee, date);

      await usePlanningStore.getState().addTaskToEntry(planningEntryId, taskId, hhmm(fit.start), hhmm(fit.end));

      // The day was switched meanwhile (setDate reset the per-day state): the task is planned,
      // but neither the undo toast nor a refetch of the old day may touch the new one.
      if (get().date !== date) return true;

      set({
        lastAssigned: {
          seq: ++assignSeq,
          planningEntryId,
          employeeId: fit.employeeId,
          taskId,
          taskName: task.name ?? '',
          employeeName: employee.name ?? '',
          start: fit.start,
          end: fit.end,
        },
      });
      // All gaps and fits change — recompute the whole day on the server.
      await get().fetchDay(date, { silent: true });
      return true;
    } catch (err) {
      if (get().date === date) {
        set((s) => ({
          data: previous,
          newlyAssignedTaskIds: removeOnce(s.newlyAssignedTaskIds, taskId),
        }));
      }
      showApiError(err);
      // A WorkDay entry may have been created before the failure — reload silently so
      // the next attempt reuses it.
      if (get().date === date) void get().fetchDay(date, { silent: true });
      return false;
    } finally {
      set({ busy: false });
    }
  },

  undo: async () => {
    const { lastAssigned, busy, date } = get();
    if (!lastAssigned || busy) return;
    // The toast (lastAssigned) stays until the removal succeeded, so a failed undo can be retried.
    set({ busy: true });
    try {
      // why: the same task can be planned twice for the employee that day — match the exact
      // block that was assigned (task + slot), never just the first block of the task.
      const findBlock = () =>
        get()
          .data?.employees?.find((e) => e.employeeId === lastAssigned.employeeId)
          ?.blocks?.find(
            (b) =>
              b.taskId === lastAssigned.taskId &&
              b.start === lastAssigned.start &&
              b.end === lastAssigned.end &&
              b.planningEntryTaskId,
          );
      let block = findBlock();
      if (!block && get().date === date) {
        await get().fetchDay(date, { silent: true });
        block = findBlock();
      }
      if (!block?.planningEntryTaskId) throw new Error('Planned task not found');

      await usePlanningStore
        .getState()
        .removeTaskFromEntry(lastAssigned.planningEntryId, block.planningEntryTaskId);

      // Day switched meanwhile: setDate already reset the per-day state.
      if (get().date !== date) return;
      set((s) => ({
        lastAssigned: s.lastAssigned?.seq === lastAssigned.seq ? null : s.lastAssigned,
        newlyAssignedTaskIds: removeOnce(s.newlyAssignedTaskIds, lastAssigned.taskId),
      }));
      await get().fetchDay(date, { silent: true });
    } catch (err) {
      showApiError(err);
    } finally {
      set({ busy: false });
    }
  },

  dismissLastAssigned: () => set({ lastAssigned: null }),

  setStartPoint: async (employeeId, value) => {
    const { data, date, busy } = get();
    if (!data || busy) return false;
    const employee = data.employees?.find((e) => e.employeeId === employeeId);
    if (!employee) return false;
    // Nothing stored without an entry — clearing is a no-op.
    if (!value && !employee.planningEntryId) return true;

    set({ busy: true });
    try {
      if (value) {
        const planningEntryId = await ensureWorkDayEntry(employee, date);
        await entriesApi.putApiV1PlanningEntriesIdStartPoint(planningEntryId, {
          latitude: value.latitude,
          longitude: value.longitude,
          label: value.label?.trim() || null,
        });
      } else {
        await entriesApi.deleteApiV1PlanningEntriesIdStartPoint(employee.planningEntryId as string);
      }
      // Travel times, fits and reasons all depend on the start point — recompute the day.
      if (get().date === date) await get().fetchDay(date, { silent: true });
      return true;
    } catch (err) {
      // why: the dialog has no coordinate fields, so field errors (invalidData) must surface as toast.
      showApiError(err, { skipIfFieldErrors: false });
      // A WorkDay entry may have been created before the failure — reload so a retry reuses it.
      if (get().date === date) void get().fetchDay(date, { silent: true });
      return false;
    } finally {
      set({ busy: false });
    }
  },
}));
